import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';

/**
 * Unterschriftsfeld: Canvas mit Pointer Events — deckt Maus, Touchpad,
 * Finger und Stift mit EINEM Codepfad ab. Exportiert die Zeichnung als
 * PNG-DataURL über `onChange` (null = leer). Bewusst ohne Fremdbibliothek;
 * devicePixelRatio-Skalierung hält die Striche auf Retina/Handys scharf.
 */
export default function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const zeichnet = useRef(false);
  const [leer, setLeer] = useState(true);

  // Canvas einmalig auf Anzeigegröße × devicePixelRatio skalieren.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const breite = canvas.clientWidth;
    const hoehe = canvas.clientHeight;
    canvas.width = breite * dpr;
    canvas.height = hoehe * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return; // jsdom/Tests und exotische Browser ohne 2D-Kontext
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1a1a5e';
  }, []);

  const pos = (e: React.PointerEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent) => {
    e.preventDefault();
    canvasRef.current!.setPointerCapture(e.pointerId);
    zeichnet.current = true;
    const ctx = canvasRef.current!.getContext('2d')!;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e: React.PointerEvent) => {
    if (!zeichnet.current) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext('2d')!;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (leer) setLeer(false);
  };

  const ende = useCallback(() => {
    if (!zeichnet.current) return;
    zeichnet.current = false;
    const canvas = canvasRef.current;
    if (canvas && !leer) onChange(canvas.toDataURL('image/png'));
  }, [leer, onChange]);

  const loeschen = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setLeer(true);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        data-testid='signature-canvas'
        style={{
          width: '100%', height: 180, touchAction: 'none', cursor: 'crosshair',
          border: '1px dashed #999', borderRadius: 6, background: '#fff',
        }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={ende}
        onPointerLeave={ende}
        onPointerCancel={ende}
      />
      <div className='d-flex justify-content-between align-items-center mt-1'>
        <small className='text-muted'>
          {leer ? 'Bitte hier unterschreiben (Maus, Touchpad oder Finger)' : 'Unterschrift erfasst ✓'}
        </small>
        <Button size='sm' variant='outline-secondary' onClick={loeschen} disabled={leer}>
          Löschen
        </Button>
      </div>
    </div>
  );
}
